import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import UserService from "../../Services/UserList";
import Swal from "sweetalert2";
import Loader from "../utils/Loader";
import * as XLSX from "xlsx"; // Import XLSX library for generating Excel files
import { saveAs } from "file-saver"; // Import file-saver to save the file
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Box,
  Button,
  TableSortLabel,
  Grid,
  InputAdornment,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchLog } from "../../redux/slices/logSlice";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { CalendarMonth, CalendarToday, Search } from "@mui/icons-material";
import TableChartIcon from "@mui/icons-material/TableChart";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import DateRangeFilter from "../utils/DateRangeFilter";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import RoleSelect from "../utils/RoleSelect";
import { fetchUserOption } from "../../redux/slices/clientSlice";
import { ClickableTableLink } from "./ClickableTableLink";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import { FY_YEAR_OPTIONS } from "../utils/FilterOptions";

const Log = () => {
  const location = useLocation();
  const navState = location.state || {};

  const dispatch = useDispatch();
  const { logData, count, status } = useSelector(state => state.logs);
  const counts = count || 0;
  
  const [month, setMonth] = useState(() => {
    if (navState && navState.months) return navState.months;
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });
  
  const [year, setYear] = useState(() => {
    if (navState.years?.length > 0) {
      const targetYrs = navState.years.map(String);
      return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    sort_by: "date",
    order: "desc",
  });
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const [roleId, setRoleId] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const { userOption } = useSelector((state) => state.client);
  const { roleOpt } = useSelector((state) => state.adminOption);

  const isUsingDateRange = Boolean(selectedFromDate || selectedToDate);
  const hasValidFilters = isUsingDateRange || Boolean(year?.length > 0 && month?.length > 0);

  useEffect(() => {
    dispatch(fetchUserOption());
  }, []);

  useEffect(() => {
    if (!hasValidFilters) {
      return;
    }
    dispatch(fetchLog({
      search: searchQuery, page, rowsPerPage,
      user: selectedUsers?.map((u) => u.value),
      month: month,
      year: year?.map((y) => y.value),
      from: selectedFromDate,
      to: selectedToDate,
      roleId: roleId?.map((u) => u.value),
      sort_by: sortBy.sort_by,
      sort_order: sortBy.order
    }))
  }, [searchQuery, page, rowsPerPage, selectedUsers, month, year, roleId, sortBy, selectedFromDate, selectedToDate, hasValidFilters])

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle the Excel download
  const handleDownloadExcel = () => {

    const filteredDataForExcel = logData?.map((item, index) => ({
      "Sr. No": index + 1,
      "User Type": item.user_type,
      "EMP Code/GST No.": item.emp_code,
      Name: item.name,
      Role: item.role_name,
      Email: item.email,
      Phone: item.phone,
      IP: item.ip,
      Date: item ? new Date(item.created_at).toLocaleDateString("en-GB") : "",
      Time: new Date(item.updated_at).toLocaleString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    }));

    // Convert data to Excel sheet
    const worksheet = XLSX.utils.json_to_sheet(filteredDataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");

    // Write the Excel file to a buffer
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    // Create a Blob from the buffer
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    // Save the file using file-saver
    saveAs(blob, "user_logs.xlsx"); // Save the Blob as an Excel file
  };

  const userOpt = userOption
    ? userOption.map((item) => ({
      value: item.id,
      label: `${item.name} ${item.last_name}`,
    }))
    : [];

    const filterConfigs = [
  { id: "user", label: "User", options: userOpt || [], selected: selectedUsers },
  { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
  { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
];

const handleMonthChange = (newMonths) => {
  setMonth(newMonths);
  if (newMonths?.length > 0 && year?.length === 0) {
    const currentYrStr = String(new Date().getFullYear());
    setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
  }
  setPage(0);
};

const handleFilterChange = (filterId, nextSelected) => {
  switch (filterId) {
    case "user": setSelectedUsers(nextSelected); break;
    case "role": setRoleId(nextSelected); break;
    case "year": 
      setYear(nextSelected); 
      if (nextSelected.length === 0) {
        setMonth([]);
      } else if (month?.length === 0) {
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
      }
      break;
    default: break;
  }
  setPage(0);
};

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const FILTER_CONFIG = [
    { options: userOpt, value: selectedUsers, onChange: setSelectedUsers, placeholder: "Select users", labelKey: "users", width: 200 },
    { options: roleOpt, value: roleId, onChange: setRoleId, placeholder: "Select role", labelKey: "role", width: 180 },
  ];

  const handleClearFilters = () => {
  setSelectedUsers([]);
  setRoleId([]);
  setSelectedFromDate(null);
  setSelectedToDate(null);
  setSearchQuery("");
  setSortBy({ sort_by: "date", order: "desc" });
  setPage(0);

  const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
  const currentCalendarMonth = new Date().getMonth() + 1;
  const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
  setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

  const currentYrStr = String(new Date().getFullYear());
  setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
};


  return (
    <MainLayout title={'Activity Log'}>

      <Box sx={{ p: 0 }}>
        <FilterBar2
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          selectedMonth={month}
          setSelectedMonth={handleMonthChange}
          searchTerm={searchQuery}
          setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
          selectedFromDate={selectedFromDate}
          setSelectedFromDate={(val) => { setSelectedFromDate(val); setPage(0); }}
          selectedToDate={selectedToDate}
          setSelectedToDate={(val) => { setSelectedToDate(val); setPage(0); }}
          onClearFilters={handleClearFilters}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={handleDownloadExcel}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2e7d32",
                  py: "2px",
                  px: "14px",
                  borderRadius: 12,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                Excel
              </Button>
            </div>
          }
        />

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ background: "#04121f", color: "white" }}>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Sr. No
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  User Type
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  EMP Code/GST No.
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "name"}
                    direction={
                      sortBy.sort_by === "name" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("name")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "role_id"}
                    direction={
                      sortBy.sort_by === "role_id" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("role_id")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Email
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Phone
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  IP
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "date"}
                    direction={
                      sortBy.sort_by === "date" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("date")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const isUsingDateRange = selectedFromDate || selectedToDate;
                const hasValidFilters = isUsingDateRange || (year?.length > 0 && month?.length > 0);
                if (!hasValidFilters) {
                  return (
                    <TableRow>
                      <TableCell colSpan={24} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  );
                }
                return logData?.length > 0 ? (

                logData?.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell align="center">{item.user_type}</TableCell>
                    <TableCell align="center">{item.empCode}</TableCell>
                    <TableCell align="center"><ClickableTableLink path="/admin/userlist" displayValue={item.name} searchValue={item.empCode} date={item.user_created_at ? item.user_created_at.substring(0, 4) : null} /></TableCell>
                    <TableCell align="center">{item.role_name}</TableCell>
                    <TableCell align="center">{item.email}</TableCell>
                    <TableCell align="center">{item.phone}</TableCell>
                    <TableCell align="center">{item.ip}</TableCell>
                    <TableCell align="center">
                      {item ? new Date(item.created_at).toLocaleDateString("en-GB") : ""}
                    </TableCell>
                    <TableCell align="center">
                      {new Date(item.updated_at).toLocaleString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </TableCell>
                  </TableRow>
                )
                )
              ) : status === 'pending' ? (
                <TableBodySkeleton cols={10} rows={10} />
              ) : (
                <TableRow>
                  <TableCell colSpan={24} align="center">
                    No logs found
                  </TableCell>
                </TableRow>

              );
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={hasValidFilters ? counts : 0}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={hasValidFilters ? [10, 20, 50, 100, counts || 0] : [10, 20, 50, 100]}
        />
      </Box>
    </MainLayout>
  );
};

export default Log;
